import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import type {
  ClientNote,
  ClientNotesResponse,
  CreateClientNoteRequest,
  UpdateClientNoteRequest,
} from "@/api/types";
import { sessionRevision } from "@/lib/session-request-scope";
import { useDashboardRequest } from "./dashboard-request";

type DashboardRequest = ReturnType<typeof useDashboardRequest>;

function notesQueryKey(dashboard: DashboardRequest, clientId: string) {
  return dashboard.queryKey("clients", clientId, "notes");
}

async function updateCachedNotes(
  queryClient: ReturnType<typeof useQueryClient>,
  queryKey: ReturnType<typeof notesQueryKey>,
  revision: number | undefined,
  update: (notes: ClientNote[]) => ClientNote[],
) {
  if (revision !== sessionRevision()) return;
  await queryClient.cancelQueries({ queryKey, exact: true });
  if (revision !== sessionRevision()) return;
  queryClient.setQueryData<ClientNotesResponse>(queryKey, (previous) =>
    previous ? { notes: update(previous.notes) } : undefined,
  );
  if (!queryClient.getQueryData(queryKey)) {
    void queryClient.invalidateQueries({ queryKey, exact: true });
  }
}

function comparableNoteTimestamp(timestamp: string): string {
  // Date normalizes offsets but stops at milliseconds; retain the API's microseconds.
  const utc = new Date(timestamp).toISOString();
  const fraction = (timestamp.match(/\.(\d+)/)?.[1] ?? "").padEnd(6, "0").slice(0, 6);
  return `${utc.slice(0, -4)}${fraction}Z`;
}

function updateExistingNote(notes: ClientNote[], accepted: ClientNote): ClientNote[] {
  const existing = notes.find((note) => note.id === accepted.id);
  if (!existing) return notes;
  if (comparableNoteTimestamp(existing.updated_at) > comparableNoteTimestamp(accepted.updated_at))
    return notes;
  return notes.map((note) => (note.id === accepted.id ? accepted : note));
}

export function useClientNotes(clientId: string) {
  const dashboard = useDashboardRequest();

  return useQuery({
    queryKey: notesQueryKey(dashboard, clientId),
    queryFn: ({ signal }) =>
      api
        .get(`dashboard/clients/${clientId}/notes`, { ...dashboard.requestOptions(), signal })
        .json<ClientNotesResponse>(),
    enabled: clientId.length > 0,
    staleTime: 30_000,
  });
}

export function useCreateClientNote(clientId: string) {
  const queryClient = useQueryClient();
  const dashboard = useDashboardRequest();

  return useMutation({
    mutationFn: (data: CreateClientNoteRequest) =>
      api
        .post(`dashboard/clients/${clientId}/notes`, { ...dashboard.requestOptions(), json: data })
        .json<{ note: ClientNote }>(),
    onMutate: () => sessionRevision(),
    onSuccess: async ({ note }, _variables, revision) => {
      const queryKey = notesQueryKey(dashboard, clientId);
      await updateCachedNotes(queryClient, queryKey, revision, (notes) =>
        notes.some((current) => current.id === note.id)
          ? updateExistingNote(notes, note)
          : [note, ...notes],
      );
      if (revision === sessionRevision() && queryClient.getQueryData(queryKey)) {
        void queryClient.invalidateQueries({ queryKey, exact: true });
      }
    },
  });
}

export function useUpdateClientNote(clientId: string) {
  const queryClient = useQueryClient();
  const dashboard = useDashboardRequest();

  return useMutation({
    mutationFn: ({ id, ...data }: UpdateClientNoteRequest & { id: string }) =>
      api
        .patch(`dashboard/clients/${clientId}/notes/${id}`, {
          ...dashboard.requestOptions(),
          json: data,
        })
        .json<{ note: ClientNote }>(),
    onMutate: () => sessionRevision(),
    onSuccess: ({ note }, _variables, revision) =>
      updateCachedNotes(queryClient, notesQueryKey(dashboard, clientId), revision, (notes) =>
        updateExistingNote(notes, note),
      ),
  });
}

export function useDeleteClientNote(clientId: string) {
  const queryClient = useQueryClient();
  const dashboard = useDashboardRequest();

  return useMutation({
    mutationFn: (id: string) =>
      api.delete(`dashboard/clients/${clientId}/notes/${id}`, dashboard.requestOptions()),
    onMutate: () => sessionRevision(),
    onSuccess: (_response, id, revision) =>
      updateCachedNotes(queryClient, notesQueryKey(dashboard, clientId), revision, (notes) =>
        notes.filter((note) => note.id !== id),
      ),
  });
}
